/**
 * Persists team billing strip values across route navigations (component remounts).
 */

export interface TeamUsageSnapshot {
  plan_name?: string | null;
  plan_briefs_included?: number;
  plan_briefs_remaining?: number;
  plan_credits_included?: number;
  plan_credits_remaining?: number;
  pack_briefs_remaining?: number;
  pack_credits_remaining?: number;
  is_trial?: boolean;
}

const STORAGE_KEY = 'xplorit_team_billing_usage';

const memory = new Map<string, TeamUsageSnapshot>();

function readStorage(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, TeamUsageSnapshot>;
    for (const [uuid, usage] of Object.entries(parsed)) {
      memory.set(uuid, usage);
    }
  } catch {
    /* ignore corrupt cache */
  }
}

function writeStorage(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const obj: Record<string, TeamUsageSnapshot> = {};
    memory.forEach((usage, uuid) => {
      obj[uuid] = usage;
    });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* quota etc */
  }
}

readStorage();

export function getCachedTeamUsage(teamUuid: string): TeamUsageSnapshot | null {
  return memory.get(teamUuid) ?? null;
}

export function setCachedTeamUsage(teamUuid: string, usage: TeamUsageSnapshot): void {
  memory.set(teamUuid, usage);
  writeStorage();
}

export function remainingPlan(u: TeamUsageSnapshot): number {
  return Math.max(0, u.plan_credits_remaining ?? u.plan_briefs_remaining ?? 0);
}

export function remainingPack(u: TeamUsageSnapshot): number {
  return u.pack_credits_remaining ?? u.pack_briefs_remaining ?? 0;
}

export function includedPlan(u: TeamUsageSnapshot): number {
  return u.plan_credits_included ?? u.plan_briefs_included ?? 0;
}

/** True when sidebar labels would change. */
export function shouldUpdateTeamUsageDisplay(
  prev: TeamUsageSnapshot | null,
  next: TeamUsageSnapshot
): boolean {
  if (!prev) return true;

  if ((prev.plan_name ?? '') !== (next.plan_name ?? '')) return true;
  if (!!prev.is_trial !== !!next.is_trial) return true;
  if (includedPlan(prev) !== includedPlan(next)) return true;

  return remainingPlan(prev) !== remainingPlan(next)
    || remainingPack(prev) !== remainingPack(next);
}
